/**
 * 路由配置 入口
 * @author LiQingSong
 */

import React, { lazy, memo, Suspense, useMemo } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import { createUseRoutes, pathKeyCreateUseRoutes } from '@/utils/router';
import { useRecoilValue } from 'recoil';
import { globalState } from '@/store/global';
import { KeepAlive } from 'react-activation';

import PageLoading from '@/components/PageLoading';

// BlankLayout
import BlankLayout from '@/layouts/BlankLayout';

// SecurityLayout
import SecurityLayout from '@/layouts/SecurityLayout';

// UniversalLayout
import UniversalLayoutRoutes from '@/layouts/UniversalLayout/routes';
import UniversalLayout from '@/layouts/UniversalLayout';

// UserLayout
import UserLayoutRoutes from '@/layouts/UserLayout/routes';
import UserLayout from '@/layouts/UserLayout';

/**
 * 配置所有路由
 */
const routes = createUseRoutes([
  {
    path: '/',
    redirect: '/home',
    children: UniversalLayoutRoutes,
  },
  {
    path: '/user',
    redirect: '/user/login',
    children: UserLayoutRoutes,
  },
  {
    path: '*',
    component: lazy(() => import('@/pages/404')),
  },
]);

/**
 * 配置框架对应的路由
 */
const layoutToRoutes = {
  UniversalLayout: pathKeyCreateUseRoutes([routes[0]]),
  UserLayout: pathKeyCreateUseRoutes([routes[1]]),
};

export const SuspenseLazy = memo(({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoading />}>{children}</Suspense>
));

export default memo(() => {
  // useRoutes 仍然可以用于那些不需要缓存的路由，比如 UserLayout 和 404 页面
  const routesElement = useRoutes(routes);
  const location = useLocation();

  const global = useRecoilValue(globalState);
  // 注意：tabNavList 的获取逻辑移到 UniversalLayout 内部，这里不再需要

  // 根路径必须先执行跳转，避免布局分支吞掉 useRoutes 返回的 Navigate。
  if (location.pathname === '/') {
    return <Navigate to="/home" replace />;
  }

  // 属于 UniversalLayout
  if (layoutToRoutes.UniversalLayout[location.pathname]) {
    return (
      <SecurityLayout>
        {/*
          关键改动：不再向 UniversalLayout 传递 children。
          它将自己负责渲染缓存的路由页面。
        */}
        <UniversalLayout />
      </SecurityLayout>
    );
  }

  // 属于 UserLayout (维持原样，因为这部分不需要缓存)
  if (layoutToRoutes.UserLayout[location.pathname]) {
    return (
      <UserLayout>
        <SuspenseLazy>{routesElement}</SuspenseLazy>
      </UserLayout>
    );
  }

  // 默认 BlankLayout (维持原样)
  return (
    <BlankLayout>
      <SuspenseLazy>{routesElement}</SuspenseLazy>
    </BlankLayout>
  );
});
